import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Badge } from "../../../components/SystemComponent/Bagde";
import {
  MdMessage,
  MdSearch,
  MdOutlineRemoveRedEye,
  MdBusiness,
  MdPerson,
  MdChevronRight,
  MdOutlineInfo,
  MdClose,
  MdFlight,
  MdSettings,
  MdDescription
} from "react-icons/md";
import { Modal } from "../../../components/SystemComponent/Modal";
import { createApi } from "../../../../core/api";
import { ChatMessageDto } from "../../../../core/api/types";

const api = createApi();

// Helper for safe date parsing
const safeDate = (input: any): Date => {
  if (!input) return new Date();
  const date = new Date(input);
  return isNaN(date.getTime()) ? new Date() : date;
};

const ChatAdminPage: React.FC = () => {
  const { setPageMenus } = useOutletContext<any>();
  
  // Data State
  const [companies, setCompanies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [inbox, setInbox] = useState<any[]>([]);
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  
  // Selection State
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  
  const [detailData, setDetailData] = useState<any>(null);
  const [sourceInfo, setSourceInfo] = useState<any>(null);
  const [cachedUserNames, setCachedUserNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [companySearch, setCompanySearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const compRes = await api.company.getAll();
      if ((compRes as any).ok) {
        const cList = (compRes as any).data?.data ?? (compRes as any).data ?? [];
        setCompanies(Array.isArray(cList) ? cList : []);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageMenus([
      {
        label: "Communication",
        items: [{ label: "Administrative Chat", route: "/dashboard/chat" }],
      },
    ]);
    loadInitialData();
  }, [loadInitialData, setPageMenus]);

  // Companies -> Users
  useEffect(() => {
    if (selectedCompanyId) {
      setUsers([]);
      api.companyUser.getByCompanyId(selectedCompanyId).then((res: any) => {
        if (res.ok) {
          const uList = res.data?.data ?? res.data ?? [];
          setUsers(Array.isArray(uList) ? uList : []);
        }
      }).catch(err => console.warn("Failed to load users", err));
      setSelectedUser(null);
    }
  }, [selectedCompanyId]);

  // Users -> Inbox
  useEffect(() => {
    if (selectedUser) {
      setInbox([]);
      setSelectedKey(null);
      api.chat.getInbox(selectedUser.id).then((res: any) => {
        if (res.ok) {
          const iList = res.data?.data ?? res.data ?? [];
          setInbox(Array.isArray(iList) ? iList : []);
        }
      }).catch(err => console.warn("Failed to load inbox", err));
    }
  }, [selectedUser]);

  // Inbox -> Messages
  useEffect(() => {
    if (selectedKey) {
      setMessages([]);
      setSourceInfo(null);
      api.chat.getConversation(selectedKey, 0, 100).then((res: any) => {
        if (res.ok && res.data) {
          const raw = res.data.data || res.data || {};
          const mList = raw.messages || (Array.isArray(raw) ? raw : []);
          setMessages(Array.isArray(mList) ? mList.filter((m: any) => m) : []);
          
          if (raw.sourceInfo) {
            setSourceInfo(raw.sourceInfo);
          }
        }
      }).catch(err => console.warn("Failed to load conversation", err));
    }
  }, [selectedKey]);

  const conversations = useMemo(() => {
    return inbox.map(item => {
      if (!item) return null;
      // Find the other participant's ID
      const otherId = item.targetUserId || item.withUserId || item.receiverUserId || item.senderUserId || item.senderId || item.receiverId;
      
      // Try to find the name in: 
      // 1. Current users list
      // 2. Already cached names (from previous background fetches)
      // 3. Inbox item's own fields
      const foundInUsers = users.find(u => (u?.id === otherId || u?.user?.id === otherId));
      const userFromList = foundInUsers ? (foundInUsers.fullName || [foundInUsers.firstName, foundInUsers.lastName].filter(Boolean).join(" ") || foundInUsers.user?.fullName) : null;
      const userNameFromCache = otherId ? cachedUserNames[otherId] : null;

      const otherUserName = 
        item.targetUserFullName || 
        item.withUserFullName || 
        userFromList ||
        userNameFromCache ||
        item.receiverFullName ||
        item.senderFullName ||
        (otherId ? `User ${otherId}` : 'Unknown');
      
      // If still "User ID" format, trigger a background fetch if not already in cache
      if (otherId && otherUserName.startsWith("User ") && !cachedUserNames[otherId]) {
        api.user.getById(otherId).then(res => {
          if (res.ok && res.data) {
             const u = res.data.data || res.data;
             const name = u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
             if (name) {
               setCachedUserNames(prev => ({ ...prev, [otherId]: name }));
             }
          }
        }).catch(() => {}); // silent fail
      }
        
      return {
        key: item.conversationKey,
        participants: `${selectedUser?.fullName || selectedUser?.firstName || 'User'} & ${otherUserName}`,
        lastDate: safeDate(item.lastMessageDate),
      };
    }).filter(Boolean); // Filter out nulls
  }, [inbox, selectedUser, users, cachedUserNames]);

  const activeConv = useMemo(() => {
    const summary = conversations.find((c: any) => c.key === selectedKey);
    if (!summary) return null;

    // Try to improve participant name from messages if it contains "User " or "Unknown"
    let displayParticipants = summary.participants;
    if (messages.length > 0 && (displayParticipants.includes("User ") || displayParticipants.includes("Unknown"))) {
      const firstMsg = messages[0] as any;
      if (firstMsg?.fromFullName && firstMsg?.toFullName) {
        const isFromSelected = firstMsg.fromUserId === selectedUser?.id;
        const otherName = isFromSelected ? firstMsg.toFullName : firstMsg.fromFullName;
        displayParticipants = `${selectedUser?.fullName || selectedUser?.firstName || 'User'} & ${otherName}`;
      }
    }

    return {
      ...summary,
      participants: displayParticipants,
      messages: [...messages].sort((a, b) => safeDate(a.createdDate).getTime() - safeDate(b.createdDate).getTime())
    };
  }, [conversations, selectedKey, messages, selectedUser]);

  return (
    <div style={S.wrap}>
      <style>{`
        .slim-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
        .slim-scroll::-webkit-scrollbar-track { background: transparent; }
        .slim-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }
        .active-item { background: #EFF6FF !important; border-left: 3px solid #3B82F6 !important; }
      `}</style>
      <div style={S.container}>
        {/* Layer 1: Companies */}
        <div style={S.layer}>
          <div style={S.layerHeader}><strong>COMPANIES</strong></div>
          <div style={S.layerSearch}>
            <input 
              style={S.layerInput} 
              placeholder="Search..." 
              value={companySearch}
              onChange={e => setCompanySearch(e.target.value)} 
            />
          </div>
          <div className="slim-scroll" style={S.layerList}>
            {companies
              .filter(c => (c.companyName || "").toLowerCase().includes(companySearch.toLowerCase()))
              .map(c => (
                <div 
                  key={c.id} 
                  onClick={() => setSelectedCompanyId(c.id)}
                  className={selectedCompanyId === c.id ? "active-item" : ""}
                  style={S.layerItem}
                >
                  <div style={S.itemText}>{c.companyName}</div>
                  <MdChevronRight color="#CBD5E1" />
                </div>
              ))}
          </div>
        </div>

        {/* Layer 2: Users */}
        <div style={{...S.layer, borderLeft: "1px solid #E2E8F0"}}>
          <div style={S.layerHeader}><strong>USERS</strong></div>
          <div style={S.layerSearch}>
            <input 
              style={S.layerInput} 
              placeholder="Search..." 
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)} 
            />
          </div>
          <div className="slim-scroll" style={S.layerList}>
            {users
              .filter(u => {
                const target = u.user || u;
                const name = `${target.firstName || ""} ${target.lastName || ""}`.toLowerCase();
                return name.includes(userSearch.toLowerCase());
              })
              .map(u => {
                const target = u.user || u;
                const name = [target.firstName, target.lastName].filter(Boolean).join(" ") || target.username || `User ${target.id}`;
                return (
                  <div 
                    key={target.id} 
                    onClick={() => setSelectedUser(target)}
                    className={selectedUser?.id === target.id ? "active-item" : ""}
                    style={S.layerItem}
                  >
                    <div style={S.itemText}>{name}</div>
                    <MdChevronRight color="#CBD5E1" />
                  </div>
                );
              })}
          </div>
        </div>

        {/* Layer 3: Conversations */}
        <div style={{...S.layer, width: 260, borderLeft: "1px solid #E2E8F0"}}>
          <div style={S.layerHeader}><strong>CONVERSATIONS</strong></div>
          <div className="slim-scroll" style={S.layerList}>
            {conversations.map(c => {
              if (!c) return null;
              return (
              <div 
                key={c.key} 
                onClick={() => setSelectedKey(c.key)}
                style={{...S.layerItem, background: selectedKey === c.key ? "#F0FDF4" : "transparent"}}
              >
                <div style={{flex: 1}}>
                  <div style={{...S.itemText, fontSize: 12}}>{c.participants}</div>
                  <div style={{fontSize: 10, color: "#94A3B8"}}>{c.lastDate.toLocaleDateString()}</div>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Main Chat Area */}
        <div style={S.main}>
          {activeConv ? (
            <>
              <div style={S.mainHeader}>
                <div>
                  <div style={S.mainTitle}>{activeConv.participants}</div>
                  <div style={S.mainSub}>Chat Monitor</div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {sourceInfo && (
                    <div 
                      onClick={() => setSourceInfo({ ...sourceInfo, _showModal: true })}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: '#EFF6FF', color: '#1D4ED8', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, border: '1px solid #BFDBFE' }}
                    >
                      <MdOutlineInfo size={16} /> Source Details
                    </div>
                  )}
                  <Badge label="Audit Mode" icon={MdOutlineRemoveRedEye} color="#D97706" bg="rgba(217,119,6,.1)" />
                </div>
              </div>

              <div className="slim-scroll" style={S.messagesArea}>
                {activeConv.messages.map((m: any) => {
                  const isSentByRef = m.fromUserId === selectedUser?.id || m.senderId === selectedUser?.id;
                  const senderName = isSentByRef ? (selectedUser.fullName || selectedUser.firstName) : (m.fromFullName || cachedUserNames[m.fromUserId] || `User ${m.fromUserId || m.senderId}`);

                  return (
                    <div key={m.id} style={{ ...S.msgRow, justifyContent: isSentByRef ? "flex-end" : "flex-start" }}>
                      <div style={{ ...S.bubble, background: isSentByRef ? "#6366F1" : "#FFFFFF", color: isSentByRef ? "#FFF" : "#1E293B" }}>
                        <div style={S.bubbleTop}>
                          <strong style={{fontSize: 11}}>{senderName}</strong>
                          <span style={{ opacity: 0.7, fontSize: 10 }}>{safeDate(m.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div style={S.bubbleText}>{m.message}</div>
                        {m.data && (
                          <div 
                            onClick={() => setDetailData(m.data)}
                            style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', background: 'rgba(0,0,0,0.05)', padding: '4px 8px', borderRadius: 4, fontSize: 10 }}
                          >
                            <MdOutlineInfo size={14} /> Data Object
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={S.footer}>Monitoring View Only</div>
            </>
          ) : (
            <div style={S.noSelection}>
              <MdMessage size={48} color="#CBD5E1" />
              <div style={{ marginTop: 12, color: "#94A3B8" }}>Please select a conversation</div>
            </div>
          )}
        </div>

        {sourceInfo && sourceInfo._showModal && (
          <SourceDetailModal data={sourceInfo} onClose={() => setSourceInfo({ ...sourceInfo, _showModal: false })} />
        )}

        {/* Data Modal */}
        <Modal open={!!detailData} onClose={() => setDetailData(null)}>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {detailData && typeof detailData === 'object' ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) 2fr', gap: '12px', padding: '10px' }}>
                {Object.entries(detailData).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#64748B', borderBottom: '1px solid #F1F5F9', padding: '4px 0' }}>{k}:</div>
                    <div style={{ fontSize: 12, color: '#1E293B', borderBottom: '1px solid #F1F5F9', padding: '4px 0', wordBreak: 'break-all' }}>
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <pre style={{ background: '#F8FAFC', padding: 10, borderRadius: 8, fontSize: 12, overflowX: 'auto' }}>
                {JSON.stringify(detailData, null, 2)}
              </pre>
            )}
          </div>
          <div style={{ marginTop: 20, textAlign: 'right' }}>
             <button onClick={() => setDetailData(null)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3B82F6', color: '#FFF', cursor: 'pointer' }}>Close</button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 20, background: "#F8FAFC", borderRadius: 16, overflow: "hidden" },
  container: { display: "flex", width: "100%", height: "100%", background: "#FFF", border: "1px solid #E2E8F0" },
  layer: { width: 200, display: "flex", flexDirection: "column", flexShrink: 0 },
  layerHeader: { padding: "16px 20px", borderBottom: "1px solid #F1F5F9", fontSize: 12 },
  layerSearch: { padding: "10px", borderBottom: "1px solid #F1F5F9" },
  layerInput: { width: "100%", height: 32, border: "1px solid #E2E8F0", borderRadius: 8, padding: "0 10px", fontSize: 12 },
  layerList: { flex: 1, overflowY: "auto" },
  layerItem: { padding: "12px 20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F8FAFC" },
  itemText: { fontSize: 12, fontWeight: 600, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  main: { flex: 1, display: "flex", flexDirection: "column", background: "#F1F5F9", borderLeft: "1px solid #E2E8F0" },
  mainHeader: { padding: "16px 24px", background: "#FFF", borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" },
  mainTitle: { fontWeight: 800, fontSize: 14, color: "#1E293B" },
  mainSub: { fontSize: 10, color: "#64748B" },
  messagesArea: { flex: 1, padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 },
  msgRow: { display: "flex" },
  bubble: { maxWidth: "75%", padding: "10px 12px", borderRadius: 12, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },
  bubbleTop: { display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 4 },
  bubbleText: { fontSize: 13, lineHeight: 1.4 },
  footer: { padding: 8, textAlign: "center", fontSize: 10, color: "#94A3B8", background: "#FFF", borderTop: "1px solid #E2E8F0" },
  noSelection: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  modalTitle: { fontSize: 14, fontWeight: 800, marginBottom: 15 }
};

// --- Sub Components for Source Modal ---

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div style={{ display: "grid", gridTemplateColumns: "28px 200px 1fr", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #F1F5F9", fontSize: 12 }}>
    <Icon size={20} color="#3B82F6" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1E293B" }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, padding: 16, background: "#FFF", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, background: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)", color: "#fff", borderRadius: 8, padding: "6px 12px" }}>
      <Icon size={18} />
      <strong style={{ fontSize: 13 }}>{title}</strong>
    </div>
    {children}
  </div>
);

const BoolBadge = ({ value }: any) => (
  <Badge label={value ? "Yes" : "No"} color={value ? "#16A34A" : "#DC2626"} bg={value ? "rgba(22,163,74,.1)" : "rgba(220,38,38,.1)"} />
);

const SourceDetailModal = ({ data, onClose }: any) => {
  const source = data.sourceInfo || data;
  const content = source.data || {};
  const typeKey = source.reservationRequestTypeKey || source._type;

  return (
    <Modal open fullScreen onClose={onClose}>
      <div style={{ padding: 24, background: '#F8FAFC', minHeight: '100vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B', marginBottom: 4 }}>Source Details</h1>
            <div style={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
              <MdOutlineInfo /> {typeKey} (ID: {source.sourceRecordId})
            </div>
          </div>
          
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))", gap: 20 }}>
          {/* General Card */}
          <Card icon={MdDescription} title="General Info">
            <InfoRow icon={MdDescription} label="Headline" value={content.headline} />
            <InfoRow icon={MdDescription} label="Description" value={content.description} />
            <InfoRow icon={MdOutlineInfo} label="Status" value={content.confirmStatus === 1 ? <Badge label="Approved" color="#16A34A" bg="#DCFCE7" /> : "Pending"} />
          </Card>

          {/* Aircraft Card */}
          {(content.aircraftType || content.aircraftTypeModel) && (
            <Card icon={MdFlight} title="Aircraft Details">
              <InfoRow icon={MdFlight} label="Type" value={content.aircraftType?.name} />
              <InfoRow icon={MdFlight} label="Model" value={content.aircraftTypeModel?.name} />
              <InfoRow icon={MdSettings} label="Engine" value={content.engineType?.code} />
              <InfoRow icon={MdSettings} label="YOM" value={content.yom} />
              <InfoRow icon={MdPerson} label="Capacity" value={content.capacity} />
            </Card>
          )}

          {/* Company Card */}
          {content.company && (
            <Card icon={MdBusiness} title="Company info">
              <InfoRow icon={MdBusiness} label="Company Name" value={content.company.companyName} />
              <InfoRow icon={MdBusiness} label="Email" value={content.company.email} />
              <InfoRow icon={MdBusiness} label="Phone" value={content.company.phone} />
              <InfoRow icon={MdBusiness} label="Tax Number" value={content.company.taxNumber} />
            </Card>
          )}

          {/* User Card */}
          {content.user && (
            <Card icon={MdPerson} title="Owner User">
              <InfoRow icon={MdPerson} label="Full Name" value={`${content.user.firstName} ${content.user.lastName}`} />
              <InfoRow icon={MdPerson} label="Username" value={content.user.username} />
              <InfoRow icon={MdPerson} label="Email" value={content.user.email} />
            </Card>
          )}

          {/* Location Card */}
          {content.location && (
            <Card icon={MdOutlineInfo} title="Location & Base">
              <InfoRow icon={MdOutlineInfo} label="Continent" value={content.location.continent?.name} />
              <InfoRow icon={MdOutlineInfo} label="Country" value={content.location.country?.name} />
              <InfoRow icon={MdOutlineInfo} label="City" value={content.location.city?.name} />
              <InfoRow icon={MdFlight} label="Airport" value={content.location.airport?.name} />
            </Card>
          )}
        </div>

        {/* Dynamic / Extra fields */}
        <div style={{ marginTop: 30 }}>
            <Card icon={MdSettings} title="Additional Technical Data">
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
                  {Object.entries(content).map(([k, v]) => {
                     const skip = ["images", "documents", "user", "company", "location", "aircraftType", "aircraftTypeModel", "engineType", "airport", "headline", "description", "id"];
                     if (skip.includes(k) || typeof v === "object") return null;
                     return (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #F1F5F9', fontSize: 12 }}>
                           <span style={{ color: '#64748B', fontWeight: 600 }}>{k.toUpperCase()}:</span>
                           <span style={{ fontWeight: 600 }}>{String(v)}</span>
                        </div>
                     )
                  })}
               </div>
            </Card>
        </div>
      </div>
    </Modal>
  )
}

export default ChatAdminPage;
